import { storage } from './storage.js';
import { account } from './account.js';

/* Spotify Connect desde la app: la sesión de Spotify (tokens) vive en este aparato; el servidor de Filoteca solo hace de puente
   para entrar (código de enlace + QR) y para renovar el acceso. Las llamadas a Spotify salen directamente de aquí.
   Hace falta Spotify Premium para mandar reproducir; sin él Spotify contesta PREMIUM_REQUIRED. */

const KEY = 'spotifyAuth';
const API = 'https://api.spotify.com/v1';
const OST = /soundtrack|banda sonora|bande originale|colonna sonora|original score|\bost\b|music from|motion picture|film music|filmmusik|trilha sonora/i;

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const err = (code) => Object.assign(new Error(code), { code });
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Elige el álbum de la banda sonora entre los resultados de Spotify, o null si ninguno parece serlo. Función pura (con test). */
export function pickAlbum(items, { title = '', composer = '' } = {}) {
    const t = norm(title);
    const c = norm(String(composer).split(',')[0]);
    let best = null;
    let bestScore = -1;
    (items || []).forEach((a, i) => {
        if (!a || !a.uri) return;
        const name = norm(a.name);
        const artists = (a.artists || []).map((x) => norm(x.name));
        let s = 0;
        if (OST.test(a.name || '')) s += 3;
        if (c && artists.some((x) => x === c || x.indexOf(c) !== -1 || (x && c.indexOf(x) !== -1))) s += 3;
        if (t && name.indexOf(t) !== -1) s += 2;
        if (a.album_type === 'album') s += 1;
        s -= i * 0.05; // a igualdad, el primero que devolvió Spotify
        if (s > bestScore) { bestScore = s; best = a; }
    });
    if (!best || bestScore < 2) return null;
    const img = (best.images || [])[1] || (best.images || [])[0] || {};
    return { id: best.id, uri: best.uri, name: best.name, artist: (best.artists || []).map((x) => x.name).join(', '), image: img.url || '', url: (best.external_urls && best.external_urls.spotify) || '', year: String(best.release_date || '').slice(0, 4) };
}

let configMemo = null;

export const spotify = {
    get auth() { return storage.get(KEY, null); },
    linked() { const a = this.auth; return !!(a && a.refresh && a.access); },
    name() { const a = this.auth; return a && a.name ? a.name : ''; },
    premium() { const a = this.auth; return !!(a && a.premium); },
    unlink() { storage.remove(KEY); },
    base() { return account.baseUrl(); },

    /** ¿El servidor tiene Spotify configurado? Se recuerda 5 minutos. */
    async configured({ force = false } = {}) {
        if (!force && configMemo && Date.now() - configMemo.at < 300000) return configMemo.enabled;
        try {
            const r = await fetch(this.base() + '/api/spotify/config', { cache: 'no-store', signal: AbortSignal.timeout(6000) });
            const j = await r.json();
            configMemo = { enabled: !!j.enabled, at: Date.now() };
        } catch (e) {
            configMemo = { enabled: false, at: Date.now() - 240000 }; // se vuelve a preguntar pronto
        }
        return configMemo.enabled;
    },
    configuredSync() { return !!(configMemo && configMemo.enabled); },

    async startLink() {
        const r = await fetch(this.base() + '/api/spotify/link', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}', signal: AbortSignal.timeout(10000) });
        if (!r.ok) throw err(r.status === 404 ? 'disabled' : 'failed');
        return r.json();
    },

    /** Espera a que alguien complete el enlace (el móvil); `isCancelled()` corta la espera. Guarda la sesión y la devuelve. */
    async waitLink(code, { isCancelled = () => false, interval = 2500, maxMs = 10 * 60 * 1000 } = {}) {
        const t0 = Date.now();
        while (Date.now() - t0 < maxMs) {
            if (isCancelled()) throw err('cancelled');
            let j;
            try {
                const r = await fetch(this.base() + '/api/spotify/link/' + encodeURIComponent(code), { cache: 'no-store', signal: AbortSignal.timeout(8000) });
                if (r.status === 410) throw err('expired');
                j = await r.json();
            } catch (e) {
                if (e.code === 'expired') throw e;
                j = { pending: true }; // red caída un momento: se sigue esperando
            }
            if (j.tokens && j.tokens.access) {
                const auth = { access: j.tokens.access, refresh: j.tokens.refresh || '', expires: Number(j.tokens.expires) || Date.now() + 3000000, name: j.tokens.name || '', premium: !!j.tokens.premium, at: Date.now() };
                storage.set(KEY, auth);
                return auth;
            }
            if (j.error) throw err(j.error === 'access_denied' || j.error === 'denied' ? 'denied' : 'failed');
            await sleep(interval);
        }
        throw err('expired');
    },

    /** Token de acceso vigente; renueva por el servidor si caducó. */
    async token({ force = false } = {}) {
        const a = this.auth;
        if (!a || !a.refresh) throw err('unlinked');
        if (!force && a.access && a.expires - 60000 > Date.now()) return a.access;
        let r;
        try {
            r = await fetch(this.base() + '/api/spotify/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh: a.refresh }), signal: AbortSignal.timeout(10000) });
        } catch (e) {
            throw err('failed');
        }
        if (r.status === 401) { this.unlink(); throw err('unlinked'); }
        if (!r.ok) throw err('failed');
        const j = await r.json();
        const next = Object.assign({}, a, { access: j.access, refresh: j.refresh || a.refresh, expires: Number(j.expires) || Date.now() + 3000000 });
        storage.set(KEY, next);
        return next.access;
    },

    async api(path, { method = 'GET', body } = {}, retry = true) {
        const tk = await this.token();
        const headers = { Authorization: 'Bearer ' + tk, Accept: 'application/json' };
        if (body) headers['Content-Type'] = 'application/json';
        let r;
        try {
            r = await fetch(API + path, { method, headers, body: body ? JSON.stringify(body) : undefined, signal: AbortSignal.timeout(12000) });
        } catch (e) {
            throw err('failed');
        }
        if (r.status === 401 && retry) {
            await this.token({ force: true });
            return this.api(path, { method, body }, false);
        }
        let data = null;
        if (r.status !== 204) { try { data = await r.json(); } catch (e) { data = null; } }
        return { status: r.status, data };
    },

    /** Busca el álbum de la banda sonora: título (original) y compositor; si no, «título soundtrack». */
    async findAlbum({ title = '', composer = '' } = {}) {
        const q1 = [title, String(composer).split(',')[0].trim()].filter(Boolean).join(' ');
        if (!q1) return null;
        const search = async (q) => {
            const r = await this.api('/search?' + new URLSearchParams({ q, type: 'album', limit: '10' }).toString());
            if (r.status === 429 || r.status >= 500) throw err('failed');
            return r.data && r.data.albums ? r.data.albums.items || [] : [];
        };
        let pick = pickAlbum(await search(q1), { title, composer });
        if (!pick) pick = pickAlbum(await search(title + ' soundtrack'), { title, composer });
        return pick;
    },

    async devices() {
        const r = await this.api('/me/player/devices');
        return (r.data && r.data.devices) || [];
    },

    /** Manda reproducir el álbum; sin aparato activo lanza 'no_device' (se elige uno con transferAndPlay). */
    async play(uri, deviceId = '') {
        const r = await this.api('/me/player/play' + (deviceId ? '?device_id=' + encodeURIComponent(deviceId) : ''), { method: 'PUT', body: { context_uri: uri } });
        if (r.status >= 200 && r.status < 300) return true;
        const reason = r.data && r.data.error && r.data.error.reason;
        if (r.status === 404 || reason === 'NO_ACTIVE_DEVICE') throw err('no_device');
        if (r.status === 403) throw err(reason === 'PREMIUM_REQUIRED' || !reason ? 'premium' : 'failed');
        throw err('failed');
    },

    async transferAndPlay(deviceId, uri) {
        const r = await this.api('/me/player', { method: 'PUT', body: { device_ids: [deviceId], play: false } });
        if (r.status === 403) throw err('premium');
        await sleep(700);
        return this.play(uri, deviceId);
    }
};
